import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { useEffect, useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { Slider } from "@lending/components/Slider/Slider";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { usePatchVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/usePatchVehicleLoanApplicationQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { getDefaultRequestedAmount } from "@lending/pages/PersonalLoan/RequestedAmount/utils";
import type { paths } from "../../../types/schema-lending";

interface RequestedAmountParams {
  min?: number;
  max?: number;
  step?: number;
}

const MIN_REQUESTED_AMOUNT_CENTS_FALLBACK = 100000;
const MAX_REQUESTED_AMOUNT_CENTS_FALLBACK = 2500000;
const REQUESTED_AMOUNT_STEP_CENTS_FALLBACK = 100000;

type VehicleLoanApplication = NonNullable<
  paths["/v2/lending/vehicle_loan_applications/{uuid}/"]["get"]["responses"][200]["content"]["application/json"]
>;

function RequestedAmount() {
  const { goBack, goNext } = useFlowNavigation();
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const { showToast } = useToastsContext();

  const { data: application, isLoading: isApplicationLoading } =
    useGetVehicleLoanApplicationQuery();
  const requestedAmountCents = (application as VehicleLoanApplication | undefined)
    ?.requested_amount_cents;

  const amountParams =
    institution.lending_vehicle_loan_requested_amount_params as RequestedAmountParams;

  const maxAmount = amountParams.max ?? MAX_REQUESTED_AMOUNT_CENTS_FALLBACK;
  const minAmount = amountParams.min ?? MIN_REQUESTED_AMOUNT_CENTS_FALLBACK;
  const amountStep = amountParams.step ?? REQUESTED_AMOUNT_STEP_CENTS_FALLBACK;
  const defaultAmountRounded = getDefaultRequestedAmount(minAmount, maxAmount, amountStep);

  const [sliderValue, setSliderValue] = useState<number>(
    requestedAmountCents ?? defaultAmountRounded
  );
  const [isSliderValueValid, setIsSliderValueValid] = useState(true);

  useEffect(() => {
    if (requestedAmountCents != null) {
      setSliderValue(requestedAmountCents);
    }
  }, [requestedAmountCents]);

  const patchVehicleLoanApplicationQuery = usePatchVehicleLoanApplicationQuery();

  const onSubmit = async () => {
    if (
      !isSliderValueValid ||
      !Number.isFinite(sliderValue) ||
      sliderValue > maxAmount ||
      sliderValue < minAmount
    ) {
      showToast({
        type: "negative",
        text: l10n.getString(
          "requested-amount-invalid-amount",
          null,
          "Please choose a valid amount."
        ),
      });
      return;
    }

    if (isApplicationLoading) {
      return;
    }

    if (!application?.id) {
      showToast({
        type: "negative",
        text: l10n.getString(
          "requested-amount-application-not-found",
          null,
          "Application not found. Please try again."
        ),
      });
      return;
    }

    try {
      await patchVehicleLoanApplicationQuery.mutateAsync({
        params: {
          path: {
            uuid: application.id,
          },
        },
        body: { requested_amount_cents: sliderValue },
      });
      goNext();
    } catch {
      showToast({
        type: "negative",
        text: l10n.getString(
          "requested-amount-save-error",
          null,
          "There was an error saving your requested amount. Please try again."
        ),
      });
    }
  };

  return (
    <PageLayout>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "requested-amount-heading",
            null,
            "How much would you like to borrow?"
          )}
          subheading={l10n.getString(
            "requested-amount-subheading",
            null,
            "Start with your preferred loan amount. You'll be able to update later."
          )}
        />
        <div className="margin--bottom--xxl">
          <Slider
            min={minAmount}
            max={maxAmount}
            step={amountStep}
            value={sliderValue}
            onChange={setSliderValue}
            onValidityChange={setIsSliderValueValid}
          />
        </div>
        <Row alignItems="center" justifyContent="space-between">
          <BackButton onClick={goBack} />
          <Button
            label={l10n.getString("button-label-next", null, "Next")}
            onClick={onSubmit}
            isLoading={patchVehicleLoanApplicationQuery.isPending}
            disabled={isApplicationLoading}
          />
        </Row>
      </PageContent>
    </PageLayout>
  );
}

export { RequestedAmount };
